// iter116 Session 2 — Page mobile de prise en charge (publique, accédée via token).
// Étapes : 1) Client → 2) Véhicule → 3) Photos → 4) Remarques → Done.
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, CheckCircle2, Camera, X, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const STEPS = ["Client", "Véhicule", "Photos", "Remarques"];

function Section({ children, title, subtitle }) {
  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-700">{label}{required && <span className="text-red-500"> *</span>}</span>
      <input
        type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-0.5 w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base focus:border-[#52a675] focus:ring-2 focus:ring-[#52a675]/30"
        style={{ fontSize: 16 }}  // anti-zoom iOS
      />
    </label>
  );
}

export default function PriseEnChargePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [step, setStep] = useState(0);
  const [client, setClient] = useState({});
  const [vehicule, setVehicule] = useState({});
  const [remarques, setRemarques] = useState("");
  const [carteGrise, setCarteGrise] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const cgInputRef = useRef(null);
  const photoInputRef = useRef(null);
  // iter227 — Sources galerie (pellicule) en plus de l'appareil photo
  const cgGalleryRef = useRef(null);
  const photoGalleryRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  const fetchSession = () => {
    setLoading(true);
    setLoadError(null);
    // Timeout 12s pour éviter de rester bloqué sur le spinner
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 12000);
    axios.get(`${API}/prise-en-charge/${token}`, { signal: ctl.signal })
      .then(({ data }) => {
        clearTimeout(timer);
        setSession(data);
        setClient(data.client_draft || {});
        setVehicule(data.vehicule_draft || {});
        setRemarques(data.remarques || "");
        setCarteGrise(data.carte_grise_photo || null);
        setPhotos(data.photos_vehicule || []);
        setLoading(false);
      })
      .catch((e) => {
        clearTimeout(timer);
        const msg = e.response?.status === 404
          ? "Lien expiré ou invalide. Demande un nouveau QR au garage."
          : e.code === "ERR_CANCELED" || e.message?.includes("abort")
            ? "Connexion lente. Vérifie ton réseau et réessaie."
            : (e.response?.data?.detail || "Impossible de charger la session. Réessaie.");
        setLoadError(msg);
        toast.error(msg);
        setLoading(false);
      });
  };

  useEffect(() => { fetchSession(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const saveClient = async () => {
    // iter128 — Si carte grise photographiée, seul le téléphone reste obligatoire
    // iter157 — Si client entreprise, raison_sociale au lieu de nom+prenom
    const isEntreprise = client.type === "entreprise";
    let required;
    if (isEntreprise) {
      required = carteGrise ? ["raison_sociale", "tel_principal"] : ["raison_sociale", "rue", "code_postal", "ville", "tel_principal"];
    } else {
      required = carteGrise ? ["tel_principal"] : ["nom", "prenom", "rue", "code_postal", "ville", "tel_principal"];
    }
    const missing = required.filter((k) => !(client[k] || "").toString().trim());
    if (missing.length) { toast.error(`Champs manquants : ${missing.join(", ")}`); return false; }
    await axios.put(`${API}/prise-en-charge/${token}/client`, client);
    return true;
  };
  const saveVehicule = async () => {
    // iter128 — Si carte grise photographiée, seul le km reste obligatoire (état au moment de la prise en charge)
    const required = carteGrise ? ["km"] : ["marque", "modele", "premiere_mec", "plaque", "vin", "km"];
    const missing = required.filter((k) => !(vehicule[k] || "").toString().trim());
    if (missing.length) { toast.error(`Champs manquants : ${missing.join(", ")}`); return false; }
    await axios.put(`${API}/prise-en-charge/${token}/vehicule`, { ...vehicule, km: parseInt(vehicule.km) || 0 });
    return true;
  };
  const saveRemarques = async () => {
    await axios.put(`${API}/prise-en-charge/${token}/remarques`, { remarques });
    return true;
  };

  const uploadFile = async (file, kind) => {
    const form = new FormData();
    form.append("file", file);
    const url = `${API}/prise-en-charge/${token}/photo/${kind === "carte_grise" ? "carte-grise" : "vehicule"}`;
    const { data } = await axios.post(url, form, { headers: { "Content-Type": "multipart/form-data" } });
    // iter130 — Si OCR carte grise a réussi, pré-remplir les champs véhicule + client
    if (kind === "carte_grise" && data.ocr_fields) {
      applyOcrFields(data.ocr_fields);
    }
    return data.path;
  };

  // iter227 — Handlers partagés par les deux sources (appareil photo / galerie)
  const handleCarteGriseFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      toast.loading("Lecture de la carte grise...", { id: "cg-ocr" });
      const p = await uploadFile(f, "carte_grise");
      setCarteGrise(p);
      toast.dismiss("cg-ocr");
    } catch (err) {
      toast.dismiss("cg-ocr");
      toast.error("Erreur upload");
    }
  };

  const handleVehiculePhotoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (photos.length >= 10) { toast.error("Maximum 10 photos"); return; }
    try {
      const p = await uploadFile(f, "vehicule");
      setPhotos([...photos, p]);
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error("Erreur upload");
    }
  };


  const applyOcrFields = (f) => {
    if (!f) return;
    const confiance = parseFloat(f.confiance || 0);
    if (confiance < 0.3) {
      toast.info("Carte grise détectée mais peu lisible — vérifie les champs.");
      return;
    }
    // Véhicule (uniquement si vide ou peu rempli)
    setVehicule((v) => ({
      ...v,
      marque: v.marque || f.marque || "",
      modele: v.modele || f.modele || "",
      plaque: v.plaque || f.plaque || "",
      vin: v.vin || f.vin || "",
      premiere_mec: v.premiere_mec || f.premiere_mec || "",
    }));
    // Client (idem)
    setClient((c) => ({
      ...c,
      nom: c.nom || f.titulaire_nom || "",
      prenom: c.prenom || f.titulaire_prenom || "",
      rue: c.rue || f.titulaire_rue || "",
      code_postal: c.code_postal || f.titulaire_npa || "",
      ville: c.ville || f.titulaire_ville || "",
    }));
    toast.success("Champs pré-remplis depuis la carte grise — vérifie et complète.");
  };

  const handleNext = async () => {
    setSubmitting(true);
    try {
      let ok = true;
      if (step === 0) ok = await saveClient();
      if (step === 1) ok = await saveVehicule();
      if (step === 3) {
        ok = await saveRemarques();
        if (ok) {
          await axios.post(`${API}/prise-en-charge/${token}/finish`);
          setDone(true);
          return;
        }
      }
      if (ok && step < STEPS.length - 1) setStep(step + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#52a675]" /></div>;
  if (loadError) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="bg-white border border-orange-200 rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
        <div className="text-4xl mb-2">⚠️</div>
        <h1 className="text-lg font-bold text-gray-900 mb-1">Impossible de charger</h1>
        <p className="text-sm text-gray-600 mb-4">{loadError}</p>
        <button onClick={fetchSession} className="w-full px-4 py-2 bg-[#52a675] text-white rounded-lg font-medium hover:bg-[#3d8a5c]" data-testid="pec-retry-load">
          Réessayer
        </button>
      </div>
    </div>
  );
  if (!session) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-gray-600">Session inconnue ou expirée.</div>;

  if (done) {
    return (
      <div className="min-h-screen bg-[#52a675]/5 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full text-center">
          <CheckCircle2 className="w-16 h-16 text-[#52a675] mx-auto mb-3" />
          <h1 className="text-xl font-bold text-gray-900 mb-1">Prise en charge terminée !</h1>
          <p className="text-sm text-gray-600 mb-4">Les informations ont été transmises au garage. Tu peux fermer cette page.</p>
          <div className="text-xs text-gray-400">CarMaintenance Meyrin · 022 980 93 93</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" richColors />
      <header className="bg-[#52a675] text-white p-4 sticky top-0 z-10">
        <h1 className="text-base font-bold">Prise en charge — Étape {step + 1}/{STEPS.length}</h1>
        <div className="text-xs text-white/80">{STEPS[step]}</div>
        <div className="mt-2 flex gap-1">
          {STEPS.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded ${i <= step ? "bg-white" : "bg-white/30"}`} />
          ))}
        </div>
      </header>

      <main className="p-4 space-y-4 pb-24">
        {step === 0 && (
          <Section title="1. Identification du client" subtitle={carteGrise ? "Téléphone obligatoire — le reste sera complété via la carte grise" : "Vérifie et complète les informations"}>
            {client.type === "entreprise" && (
              <div className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded mb-1" data-testid="pec-client-entreprise-badge">
                Client entreprise
              </div>
            )}
            {client.type === "entreprise" ? (
              <Field label="Raison sociale" value={client.raison_sociale} onChange={(v) => setClient({ ...client, raison_sociale: v })} required />
            ) : (
              <>
                <Field label="Nom" value={client.nom} onChange={(v) => setClient({ ...client, nom: v })} required={!carteGrise} />
                <Field label="Prénom" value={client.prenom} onChange={(v) => setClient({ ...client, prenom: v })} required={!carteGrise} />
              </>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Field label="Rue" value={client.rue} onChange={(v) => setClient({ ...client, rue: v })} required={!carteGrise} /></div>
              <Field label="N°" value={client.numero} onChange={(v) => setClient({ ...client, numero: v })} />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="NPA" value={client.code_postal} onChange={(v) => setClient({ ...client, code_postal: v })} required={!carteGrise} />
              <div className="col-span-2"><Field label="Ville" value={client.ville} onChange={(v) => setClient({ ...client, ville: v })} required={!carteGrise} /></div>
            </div>
            <Field label="Téléphone" value={client.tel_principal} onChange={(v) => setClient({ ...client, tel_principal: v })} required type="tel" placeholder="+41 79 ..." />
            <Field label="E-mail (facultatif)" value={client.email} onChange={(v) => setClient({ ...client, email: v })} type="email" />
          </Section>
        )}

        {step === 1 && (
          <Section title="2. Véhicule" subtitle={carteGrise ? "Kilométrage obligatoire — le reste sera complété via la carte grise" : "Tous les champs sont obligatoires"}>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
              <div className="text-xs text-blue-900 mb-2 font-medium">📷 Pour aller plus vite : photo de la carte grise (appareil ou galerie), puis saisis juste le kilométrage.</div>
              <input ref={cgInputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={handleCarteGriseFile} data-testid="pec-cg-input-camera" />
              <input ref={cgGalleryRef} type="file" accept="image/*" className="hidden"
                onChange={handleCarteGriseFile} data-testid="pec-cg-input-gallery" />
              <div className="flex gap-2">
                <button onClick={() => cgInputRef.current?.click()} className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5" data-testid="pec-cg-upload">
                  <Camera className="w-4 h-4" /> {carteGrise ? "Reprendre" : "Photo"}
                </button>
                <button onClick={() => cgGalleryRef.current?.click()} className="flex-1 px-3 py-2 bg-white border border-blue-300 text-blue-700 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-1.5" data-testid="pec-cg-upload-gallery">
                  <ImageIcon className="w-4 h-4" /> Galerie
                </button>
              </div>
              {carteGrise && (
                <div className="mt-2 text-xs text-[#2d5f3f] font-semibold" data-testid="pec-cg-done">✓ Carte grise envoyée</div>
              )}
            </div>
            <Field label="Marque" value={vehicule.marque} onChange={(v) => setVehicule({ ...vehicule, marque: v })} required={!carteGrise} />
            <Field label="Type / Modèle" value={vehicule.modele} onChange={(v) => setVehicule({ ...vehicule, modele: v })} required={!carteGrise} />
            <Field label="Première mise en circulation" value={vehicule.premiere_mec} onChange={(v) => setVehicule({ ...vehicule, premiere_mec: v })} required={!carteGrise} type="date" />
            <Field label="Plaque" value={vehicule.plaque} onChange={(v) => setVehicule({ ...vehicule, plaque: v.toUpperCase() })} required={!carteGrise} />
            <Field label="N° châssis (VIN)" value={vehicule.vin} onChange={(v) => setVehicule({ ...vehicule, vin: v.toUpperCase().replace(/[^A-Z0-9]/g, "") })} required={!carteGrise} />
            <Field label="Kilométrage" value={vehicule.km} onChange={(v) => setVehicule({ ...vehicule, km: v.replace(/\D/g, "") })} required type="tel" />
          </Section>
        )}

        {step === 2 && (
          <Section title="3. Photos du véhicule" subtitle="Facultatif — recommandé : 4 angles + max 6 photos supplémentaires (10 max)">
            <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={handleVehiculePhotoFile} data-testid="pec-photo-input-camera" />
            <input ref={photoGalleryRef} type="file" accept="image/*" className="hidden"
              onChange={handleVehiculePhotoFile} data-testid="pec-photo-input-gallery" />
            <div className="flex gap-2">
              <button onClick={() => photoInputRef.current?.click()} className="flex-1 px-4 py-3 bg-[#52a675] text-white rounded-xl font-medium flex items-center justify-center gap-2" data-testid="pec-photo-add">
                <Camera className="w-5 h-5" /> Photo
              </button>
              <button onClick={() => photoGalleryRef.current?.click()} className="flex-1 px-4 py-3 bg-white border border-[#52a675]/40 text-[#2d5f3f] rounded-xl font-medium flex items-center justify-center gap-2" data-testid="pec-photo-add-gallery">
                <ImageIcon className="w-5 h-5" /> Galerie
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-xs text-gray-500 relative">
                  <Camera className="w-6 h-6" /><span className="absolute bottom-1 right-1 bg-white text-[10px] px-1 rounded">{i+1}</span>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500">{photos.length}/10 photos</div>
          </Section>
        )}

        {step === 3 && (
          <Section title="4. Remarques du client" subtitle="Note ici toute information utile au mécanicien">
            <textarea value={remarques} onChange={(e) => setRemarques(e.target.value)} rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:border-[#52a675]"
              style={{ fontSize: 16 }}
              placeholder="Ex: bruit en virage à gauche, voyant allumé depuis lundi…" />
          </Section>
        )}
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} disabled={submitting} className="px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700">
            ← Retour
          </button>
        )}
        <button onClick={handleNext} disabled={submitting} className="flex-1 px-4 py-2.5 bg-[#52a675] text-white rounded-xl font-semibold disabled:opacity-50" data-testid="pec-next">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
          {step === STEPS.length - 1 ? "✓ Terminer la prise en charge" : "Suivant →"}
        </button>
      </footer>
    </div>
  );
}
